import React from "react";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import useSubscriptions from "@/hooks/useSubscriptions";
import IntegrationNotEnabled from "./IntegrationNotEnabled";
import type { IntegrationMeta } from "@/data/integrationUpgradeMeta";

/**
 * Gates an integration config page behind a v20 AdminFeatures flag.
 * When the flag is false the child component never mounts — no API calls fire.
 *
 * Pass `meta` from INTEGRATION_META to show plan badge + benefit bullets:
 *   import { INTEGRATION_META } from "@/data/integrationUpgradeMeta";
 *   <GatedAdminFeature flagKey="enableHubSpot" name="HubSpot CRM" meta={INTEGRATION_META.enableHubSpot}>
 *     <HubSpot />
 *   </GatedAdminFeature>
 */
export function GatedAdminFeature({
  flagKey,
  name,
  description,
  meta,
  children,
}: {
  flagKey: keyof ReturnType<typeof useAdminFeatures>;
  name: string;
  description?: string;
  meta?: IntegrationMeta;
  children: React.ReactNode;
}) {
  const features = useAdminFeatures();
  if (features.loading) return null;
  if (!features[flagKey]) {
    return (
      <IntegrationNotEnabled
        name={name}
        description={meta?.description ?? description}
        plan={meta?.plan}
        benefits={meta?.benefits}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Gates an integration config page behind a v10 subscription flag.
 * When the flag is false the child component never mounts — no API calls fire.
 *
 * Pass `meta` from INTEGRATION_META to show plan badge + benefit bullets:
 *   import { INTEGRATION_META } from "@/data/integrationUpgradeMeta";
 *   <GatedSubscriptionFeature flagKey="enableFormstack" name="Formstack" meta={INTEGRATION_META.enableFormstack}>
 *     <Formstack />
 *   </GatedSubscriptionFeature>
 */
export function GatedSubscriptionFeature({
  flagKey,
  name,
  description,
  meta,
  children,
}: {
  flagKey: string;
  name: string;
  description?: string;
  meta?: IntegrationMeta;
  children: React.ReactNode;
}) {
  const { subscription, loading } = useSubscriptions();
  if (loading) return null;
  if (!subscription?.[flagKey as keyof typeof subscription]) {
    return (
      <IntegrationNotEnabled
        name={name}
        description={meta?.description ?? description}
        plan={meta?.plan}
        benefits={meta?.benefits}
      />
    );
  }
  return <>{children}</>;
}
